// pkg198 — "Payment methods & autopay" sidebar item (saved LawPay methods, autopay, plans) — delivered 2026-11-01
// pkg172a — "Invoices & Payments" nav link (client billing page) — delivered 2026-10-03
// pkg134 — Terms of Service + Privacy Policy links in the sidebar footer (always visible on every client page; public pkg84 routes, new tab) — delivered 2026-07-27
// pkg85 — "© {year} LawMighty, LLC" line added to the sidebar footer (LawMighty, LLC formed in Tennessee, July 2026; the platform now has a legal owner of record) — delivered 2026-07-15
// pkg65 — paused-portal notice when the FIRM's subscription lapses (402 event from api.js); clients see a calm 'contact your firm' message, never billing details (D-65d) — delivered 2026-07-11
// pkg32 — Emergency Alert banner rendered at top of main content (firm-wide notice)
// pkg21 — sidebar links for assigned firm-private AI modules (/ai-modules/:id) — delivered 2026-06-11
import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../utils/api';
import { useFirmName } from '../contexts/FirmSettingsContext';
import { useClientModules } from '../contexts/ClientModulesContext';
import AlertBanner from './AlertBanner';

// Resolve a NavLink path for any module. Built-ins use module.route (the
// React Router path the backend seeded — '/demographic', '/parenting-plan',
// etc.). Content modules use /modules/:id, which routes to ContentModulePage.
function moduleHref(m) {
  if (m.module_type === 'builtin') return m.route || '/';
  return `/modules/${m.id}`;
}

export default function Layout() {
  const { client, setClient, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const firmName = useFirmName();
  const { modules, firmModules, loading: modulesLoading } = useClientModules();

  // pkg65 — the FIRM's LawMighty subscription lapsed (402 from any API call).
  // Clients can't fix billing, so the message is calm and non-financial.
  const [portalPaused, setPortalPaused] = useState(false);
  useEffect(() => {
    const onPause = () => setPortalPaused(true);
    window.addEventListener('lm:subscription-required', onPause);
    return () => window.removeEventListener('lm:subscription-required', onPause);
  }, []);

  // The discovery_response built-in module is a "kill switch" but the link
  // ALSO shouldn't appear when the client has no responses yet — same rule
  // as before. Probe the discovery list endpoint independently. If the
  // discovery module isn't assigned at all, this probe still runs but its
  // result is moot (the link is hidden either way).
  const [hasVisibleDiscovery, setHasVisibleDiscovery] = useState(false);
  const hasDiscoveryModule = modules.some(m => m.key === 'discovery_response');

  // Refresh client info on mount so the layout reflects the latest state.
  useEffect(() => {
    api.me().then(data => setClient(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!hasDiscoveryModule) {
      setHasVisibleDiscovery(false);
      return;
    }
    api.listClientDiscovery()
      .then(rows => setHasVisibleDiscovery(Array.isArray(rows) && rows.length > 0))
      .catch(() => setHasVisibleDiscovery(false));
  }, [hasDiscoveryModule]);

  // Browser tab: "[Firm] – Client Portal" while inside the client area.
  useEffect(() => {
    if (firmName) document.title = `${firmName} – Client Portal`;
    return () => { document.title = firmName || 'Firm Portal'; };
  }, [firmName]);

  function handleNav() { setOpen(false); }

  // Per-module filter for "should this link render?":
  //   * discovery_response: hide if no visible responses (avoids empty state)
  //   * everything else: render if the module is in the list at all
  // The API already filters to active + enabled modules for this client, so
  // there's no per-case-type logic here anymore — the backend is the source
  // of truth.
  function shouldRenderModule(m) {
    if (m.key === 'discovery_response') return hasVisibleDiscovery;
    return true;
  }

  const visibleModules = modules.filter(shouldRenderModule);

  return (
    <div className="portal-layout">
      <aside className={`sidebar${open ? ' open' : ''}`}>
        <div className="mobile-topbar">
          <span className="mobile-topbar-title">{firmName}</span>
          <button className="mobile-menu-btn" onClick={() => setOpen(o => !o)} aria-label="Menu">
            {open ? '✕' : '☰'}
          </button>
        </div>

        <div className="sidebar-brand">
          <h1>{firmName}</h1>
          <p>Client Portal</p>
        </div>

        <nav className="sidebar-nav" onClick={handleNav}>
          <NavLink to="/" end>Dashboard</NavLink>
          {/* While the modules list is loading we deliberately render only
              Dashboard + Account, matching the AdminLayout pattern of "don't
              flash a fuller menu and then collapse it." */}
          {!modulesLoading && visibleModules.map(m => (
            <NavLink key={m.id} to={moduleHref(m)}>{m.label}</NavLink>
          ))}
          {/* pkg21 — firm-private AI modules assigned to this client. The
              API already filters to active + assigned, so every entry
              renders. Routed to AiModulePage via /ai-modules/:id. */}
          {!modulesLoading && firmModules.map(m => (
            <NavLink key={`fm-${m.id}`} to={`/ai-modules/${m.id}`}>{m.label}</NavLink>
          ))}
          <NavLink to="/billing">Invoices &amp; Payments</NavLink>{/* pkg172a */}
          <NavLink to="/payment-methods">Payment methods &amp; autopay</NavLink>{/* pkg198 */}
          <NavLink to="/account">Account Settings</NavLink>
        </nav>

        <div className="sidebar-footer">
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', fontFamily: 'Arial, sans-serif', marginBottom: 8 }}>
            {client?.name}
          </div>
          <button onClick={() => { logout(); navigate('/login'); }}>Sign Out</button>
          {/* pkg134 — always-available legal links (Ryan: users need Terms &
              Privacy accessible at all times; web-portal parity with mobile
              pkg131). Public pkg84 routes; new tab so the session view stays. */}
          <div style={{ fontSize: 10, fontFamily: 'Arial, sans-serif', marginTop: 8, textAlign: 'center' }}>
            <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.5)' }}>Terms of Service</a>
            <span style={{ color: 'rgba(255,255,255,0.3)', margin: '0 6px' }}>·</span>
            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.5)' }}>Privacy Policy</a>
          </div>
          {/* pkg85 — platform copyright: LawMighty, LLC (TN) is the entity of record */}
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', fontFamily: 'Arial, sans-serif', marginTop: 10, textAlign: 'center' }}>
            © {new Date().getFullYear()} LawMighty, LLC
          </div>
        </div>
      </aside>

      <main className="main-content">
        <AlertBanner />
        {portalPaused ? (
          <div style={{ maxWidth: 520, margin: '48px auto', textAlign: 'center', fontFamily: 'Arial, sans-serif' }}>
            <h1 className="page-title" style={{ marginBottom: 8 }}>Portal temporarily unavailable</h1>
            <p style={{ fontSize: 14, color: '#555', lineHeight: 1.6 }}>
              {firmName ? `The ${firmName} client portal` : 'This client portal'} is temporarily
              unavailable. Your information is saved. Please contact your firm directly with any questions.
            </p>
          </div>
        ) : (
          <Outlet />
        )}
      </main>
    </div>
  );
}
